import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { adminService } from '../../../../services';
import { useAuth } from '../../../../contexts/AuthContext';
import { canManageUser } from '../../../common/RoleGuard';
import type { AccountStatus, User } from '../../../../types/auth';
import './AdminUsers.css';

// ── Helpers ──────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const ROLE_CONFIG: Record<string, { label: string; className: string }> = {
  user: { label: 'Utente', className: 'au-badge--user' },
  admin: { label: 'Admin', className: 'au-badge--admin' },
  superadmin: { label: 'SuperAdmin', className: 'au-badge--superadmin' },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  full: { label: 'Completo', className: 'au-badge--full' },
  free_only: { label: 'Free', className: 'au-badge--free' },
  suspended: { label: 'Bloccato', className: 'au-badge--blocked' },
};

type SortField = 'firstName' | 'email' | 'createdAt' | 'role';
type SortDir = 'asc' | 'desc';
type RoleFilter = 'all' | 'user' | 'admin' | 'superadmin';

// ── Skeleton ─────────────────────────────────────────────────

const TableSkeleton: React.FC = () => (
  <div className="au-skeleton-rows">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="au-skeleton-row">
        <div className="au-skeleton au-skeleton--avatar" />
        <div className="au-skeleton-cell">
          <div className="au-skeleton au-skeleton--name" />
          <div className="au-skeleton au-skeleton--email" />
        </div>
        <div className="au-skeleton au-skeleton--badge" />
        <div className="au-skeleton au-skeleton--badge" />
        <div className="au-skeleton au-skeleton--date" />
        <div className="au-skeleton au-skeleton--actions" />
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const basePath = location.pathname.startsWith('/superadmin')
    ? '/superadmin'
    : '/admin';

  // Filtro "iscritti a un corso" arrivato da AdminCourses (click su "N iscritti")
  const [searchParams, setSearchParams] = useSearchParams();
  const courseFilter = searchParams.get('corso');
  const courseFilterTitle =
    (location.state as { courseTitle?: string } | null)?.courseTitle ??
    courseFilter;

  const clearCourseFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('corso');
      return next;
    });
  };

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previousStatuses, setPreviousStatuses] = useState<
    Record<string, AccountStatus>
  >({});

  // Filtri
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Azione blocco
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [resettingPwdId, setResettingPwdId] = useState<string | null>(null);
  const [resetPwdSentId, setResetPwdSentId] = useState<string | null>(null);

  useEffect(() => {
    adminService
      .getUsers()
      .then(setUsers)
      .catch(() => setError('Impossibile caricare gli utenti.'))
      .finally(() => setLoading(false));
  }, []);

  // ── Filtro + sort ────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...users];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.firstName.toLowerCase().includes(q) ||
          u.lastName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
      );
    }

    if (roleFilter !== 'all') {
      list = list.filter((u) => u.role === roleFilter);
    }

    if (courseFilter) {
      list = list.filter((u) => u.purchasedCourseIds?.includes(courseFilter));
    }

    list.sort((a, b) => {
      let va = '',
        vb = '';
      if (sortField === 'firstName') {
        va = `${a.firstName} ${a.lastName}`;
        vb = `${b.firstName} ${b.lastName}`;
      } else if (sortField === 'email') {
        va = a.email;
        vb = b.email;
      } else if (sortField === 'createdAt') {
        va = a.createdAt;
        vb = b.createdAt;
      } else if (sortField === 'role') {
        va = a.role;
        vb = b.role;
      }
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return list;
  }, [users, search, roleFilter, courseFilter, sortField, sortDir]);

  // ── Handlers ────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleBlock = async (userId: string, currentlyBlocked: boolean) => {
    setBlockingId(userId);
    try {
      await adminService.blockUser(userId, !currentlyBlocked);
      setUsers((prev) =>
        prev.map((u) => {
          if (u.id !== userId) return u;

          if (currentlyBlocked) {
            // sblocco → ripristina lo stato precedente
            const restored = previousStatuses[userId] ?? 'full';
            return { ...u, accountStatus: restored };
          } else {
            // blocco → salva lo stato corrente e imposta 'suspended'
            setPreviousStatuses((ps) => ({ ...ps, [userId]: u.accountStatus }));
            return { ...u, accountStatus: 'suspended' };
          }
        })
      );
    } catch {
      // TODO: toast errore
    } finally {
      setBlockingId(null);
    }
  };

  const handleDelete = async (userId: string) => {
    setDeletingId(userId);
    try {
      await adminService.deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch {
      // TODO: mostrare toast errore
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const handleResetPassword = async (userId: string) => {
    setResettingPwdId(userId);
    try {
      await adminService.resetUserPassword(userId);
      setResetPwdSentId(userId);
      setTimeout(
        () => setResetPwdSentId((id) => (id === userId ? null : id)),
        4000
      );
    } catch {
      // TODO: mostrare toast errore
    } finally {
      setResettingPwdId(null);
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field)
      return (
        <i className="bi bi-arrow-down-up au-sort-icon au-sort-icon--inactive" />
      );
    return (
      <i
        className={`bi ${sortDir === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} au-sort-icon`}
      />
    );
  };

  const initials = (u: User) =>
    `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase();

  return (
    <div className="au-page">
      {/* ── Header ── */}
      <div className="au-page-header">
        <div>
          <h1 className="au-page-title">Utenti</h1>
          <p className="au-page-sub">
            {loading ? '…' : `${filtered.length} di ${users.length} utenti`}
          </p>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="au-toolbar">
        {/* Search */}
        <div className="au-search-wrap">
          <i className="bi bi-search au-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="au-search"
            placeholder="Cerca per nome o email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca utenti"
          />
          {search && (
            <button
              className="au-search-clear"
              onClick={() => setSearch('')}
              aria-label="Cancella ricerca"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        {/* Role filter */}
        <div className="au-filters">
          {(['all', 'user', 'admin', 'superadmin'] as RoleFilter[]).map((r) => (
            <button
              key={r}
              className={`au-filter-btn ${roleFilter === r ? 'au-filter-btn--active' : ''}`}
              onClick={() => setRoleFilter(r)}
            >
              {r === 'all' ? 'Tutti' : (ROLE_CONFIG[r]?.label ?? r)}
              {r !== 'all' && (
                <span className="au-filter-count">
                  {users.filter((u) => u.role === r).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Filtro corso attivo (arrivato da AdminCourses) ── */}
      {courseFilter && (
        <div className="au-course-filter">
          <i className="bi bi-collection-play me-2" />
          Iscritti a <strong>{courseFilterTitle}</strong>
          <button
            className="au-course-filter__clear"
            onClick={clearCourseFilter}
            aria-label="Rimuovi filtro corso"
          >
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="au-error" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {/* ── Tabella desktop ── */}
      {loading ? (
        <TableSkeleton />
      ) : (
        <>
          {filtered.length === 0 ? (
            <div className="au-empty">
              <i className="bi bi-person-slash au-empty-icon" />
              <p className="au-empty-title">Nessun utente trovato</p>
              <p className="au-empty-sub">
                Prova a cambiare i filtri di ricerca.
              </p>
              <button
                className="au-action-btn"
                onClick={() => {
                  setSearch('');
                  setRoleFilter('all');
                }}
              >
                Reimposta filtri
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="au-table-wrap">
                <table className="au-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          className="au-th-btn"
                          onClick={() => handleSort('firstName')}
                        >
                          Utente <SortIcon field="firstName" />
                        </button>
                      </th>
                      <th>
                        <button
                          className="au-th-btn"
                          onClick={() => handleSort('email')}
                        >
                          Email <SortIcon field="email" />
                        </button>
                      </th>
                      <th>
                        <button
                          className="au-th-btn"
                          onClick={() => handleSort('role')}
                        >
                          Ruolo <SortIcon field="role" />
                        </button>
                      </th>
                      <th>Stato</th>
                      <th>
                        <button
                          className="au-th-btn"
                          onClick={() => handleSort('createdAt')}
                        >
                          Iscritto <SortIcon field="createdAt" />
                        </button>
                      </th>
                      <th aria-label="Azioni" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const isBlocked = u.accountStatus === 'suspended';
                      const isBlocking = blockingId === u.id;
                      const isDeleting = deletingId === u.id;
                      const roleConf = ROLE_CONFIG[u.role];
                      const statusConf =
                        STATUS_CONFIG[u.accountStatus ?? 'full'];
                      const canManage = currentUser
                        ? canManageUser(currentUser, u)
                        : false;

                      return (
                        <tr
                          key={u.id}
                          className={isBlocked ? 'au-row--blocked' : ''}
                        >
                          {/* Avatar + nome */}
                          <td>
                            <div className="au-user-cell">
                              <div className="au-avatar">
                                {u.avatarUrl ? (
                                  <img src={u.avatarUrl} alt={u.firstName} />
                                ) : (
                                  initials(u)
                                )}
                              </div>
                              <div>
                                <p className="au-user-name">
                                  {u.firstName} {u.lastName}
                                </p>
                                {!u.emailConfirmed && (
                                  <span className="au-unconfirmed">
                                    <i className="bi bi-exclamation-circle me-1" />
                                    Email non confermata
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="au-email-cell">{u.email}</td>

                          {/* Ruolo */}
                          <td>
                            <span className={`au-badge ${roleConf?.className}`}>
                              {roleConf?.label ?? u.role}
                            </span>
                          </td>

                          {/* Stato */}
                          <td>
                            <span
                              className={`au-badge ${statusConf?.className}`}
                            >
                              {statusConf?.label ?? u.accountStatus}
                            </span>
                          </td>

                          {/* Data */}
                          <td className="au-date-cell">
                            {formatDate(u.createdAt)}
                          </td>

                          {/* Azioni */}
                          <td>
                            <div className="au-row-actions">
                              {/* Dettaglio */}
                              <button
                                className="au-icon-btn"
                                onClick={() =>
                                  navigate(`${basePath}/utenti/${u.id}`)
                                }
                                title="Vedi dettaglio"
                                aria-label="Vedi dettaglio utente"
                              >
                                <i className="bi bi-eye" />
                              </button>

                              {/* Reimposta password */}
                              {canManage && u.authProvider === 'email' && (
                                <button
                                  className="au-icon-btn"
                                  onClick={() => handleResetPassword(u.id)}
                                  disabled={resettingPwdId === u.id}
                                  title="Reimposta password"
                                  aria-label="Reimposta password"
                                >
                                  {resettingPwdId === u.id ? (
                                    <span
                                      className="au-spinner"
                                      aria-hidden="true"
                                    />
                                  ) : resetPwdSentId === u.id ? (
                                    <i className="bi bi-check2" />
                                  ) : (
                                    <i className="bi bi-envelope" />
                                  )}
                                </button>
                              )}

                              {/* Blocca/Sblocca — mai su se stessi o su un ruolo pari/superiore */}
                              {canManage && (
                                <button
                                  className={`au-icon-btn ${isBlocked ? 'au-icon-btn--warn' : ''}`}
                                  onClick={() => handleBlock(u.id, isBlocked)}
                                  disabled={isBlocking}
                                  title={
                                    isBlocked
                                      ? 'Sblocca utente'
                                      : 'Blocca utente'
                                  }
                                  aria-label={
                                    isBlocked
                                      ? 'Sblocca utente'
                                      : 'Blocca utente'
                                  }
                                >
                                  {isBlocking ? (
                                    <span
                                      className="au-spinner"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <i
                                      className={`bi ${isBlocked ? 'bi-unlock' : 'bi-lock'}`}
                                    />
                                  )}
                                </button>
                              )}

                              {/* Elimina — mai su se stessi o su un ruolo pari/superiore */}
                              {canManage &&
                                (confirmDelete === u.id ? (
                                  <div className="au-confirm-delete">
                                    <span className="au-confirm-label">
                                      Sicuro?
                                    </span>
                                    <button
                                      className="au-icon-btn au-icon-btn--danger"
                                      onClick={() => handleDelete(u.id)}
                                      disabled={isDeleting}
                                      aria-label="Conferma eliminazione"
                                    >
                                      {isDeleting ? (
                                        <span
                                          className="au-spinner"
                                          aria-hidden="true"
                                        />
                                      ) : (
                                        <i className="bi bi-check2" />
                                      )}
                                    </button>
                                    <button
                                      className="au-icon-btn"
                                      onClick={() => setConfirmDelete(null)}
                                      aria-label="Annulla"
                                    >
                                      <i className="bi bi-x" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="au-icon-btn au-icon-btn--danger"
                                    onClick={() => setConfirmDelete(u.id)}
                                    title="Elimina utente"
                                    aria-label="Elimina utente"
                                  >
                                    <i className="bi bi-trash" />
                                  </button>
                                ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="au-mobile-list">
                {filtered.map((u) => {
                  const isBlocked = u.accountStatus === 'suspended';
                  const roleConf = ROLE_CONFIG[u.role];
                  const statusConf = STATUS_CONFIG[u.accountStatus ?? 'full'];
                  const canManage = currentUser
                    ? canManageUser(currentUser, u)
                    : false;

                  return (
                    <div
                      key={u.id}
                      className={`au-mobile-card ${isBlocked ? 'au-mobile-card--blocked' : ''}`}
                    >
                      <div className="au-mobile-card__top">
                        <div className="au-user-cell">
                          <div className="au-avatar">
                            {u.avatarUrl ? (
                              <img src={u.avatarUrl} alt={u.firstName} />
                            ) : (
                              initials(u)
                            )}
                          </div>
                          <div>
                            <p className="au-user-name">
                              {u.firstName} {u.lastName}
                            </p>
                            <p className="au-email-cell">{u.email}</p>
                          </div>
                        </div>
                        <div className="au-mobile-card__badges">
                          <span className={`au-badge ${roleConf?.className}`}>
                            {roleConf?.label}
                          </span>
                          <span className={`au-badge ${statusConf?.className}`}>
                            {statusConf?.label}
                          </span>
                        </div>
                      </div>

                      <div className="au-mobile-card__footer">
                        <span className="au-date-cell">
                          <i className="bi bi-calendar3 me-1" />
                          {formatDate(u.createdAt)}
                        </span>
                        <div className="au-row-actions">
                          <button
                            className="au-icon-btn"
                            onClick={() =>
                              navigate(`${basePath}/utenti/${u.id}`)
                            }
                            aria-label="Vedi dettaglio"
                            title="Dettaglio"
                          >
                            <i className="bi bi-eye" />
                          </button>
                          {canManage && u.authProvider === 'email' && (
                            <button
                              className="au-icon-btn"
                              onClick={() => handleResetPassword(u.id)}
                              disabled={resettingPwdId === u.id}
                              aria-label="Reimposta password"
                              title="Reimposta password"
                            >
                              {resetPwdSentId === u.id ? (
                                <i className="bi bi-check2" />
                              ) : (
                                <i className="bi bi-envelope" />
                              )}
                            </button>
                          )}
                          {canManage && (
                            <button
                              className={`au-icon-btn ${isBlocked ? 'au-icon-btn--warn' : ''}`}
                              onClick={() => handleBlock(u.id, isBlocked)}
                              disabled={blockingId === u.id}
                              aria-label={isBlocked ? 'Sblocca' : 'Blocca'}
                            >
                              <i
                                className={`bi ${isBlocked ? 'bi-unlock' : 'bi-lock'}`}
                              />
                            </button>
                          )}
                          {canManage && (
                            <button
                              className="au-icon-btn au-icon-btn--danger"
                              onClick={() => setConfirmDelete(u.id)}
                              aria-label="Elimina"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* Conferma delete mobile */}
      {confirmDelete && (
        <div className="au-delete-overlay" role="dialog" aria-modal="true">
          <div className="au-delete-dialog">
            <p className="au-delete-dialog__title">
              <i className="bi bi-exclamation-triangle me-2" />
              Eliminare questo utente?
            </p>
            <p className="au-delete-dialog__sub">
              L'operazione è irreversibile.
            </p>
            <div className="au-delete-dialog__actions">
              <button
                className="au-action-btn"
                onClick={() => setConfirmDelete(null)}
              >
                Annulla
              </button>
              <button
                className="au-action-btn au-action-btn--danger"
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deletingId}
              >
                {deletingId ? (
                  <>
                    <span className="au-spinner me-1" />
                    Eliminazione…
                  </>
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
